import type { TimeWindow } from "./isWithinWorkingHours";

const toMinutes = (hour: number, minutes: number): number =>
  hour * 60 + minutes;

const fromMinutes = (
  totalMinutes: number
): { hour: number; minutes: number } => ({
  hour: Math.floor(totalMinutes / 60),
  minutes: totalMinutes % 60
});

const toWindow = (start: number, end: number): TimeWindow => {
  const { hour: startHour, minutes: startMinutes } = fromMinutes(start);
  const { hour: endHour, minutes: endMinutes } = fromMinutes(end);
  return { startHour, startMinutes, endHour, endMinutes };
};

/**
 * Caps a reservation time window to the office open hours: a start before
 * opening moves to the opening time, an end after closing moves to the
 * closing time.
 *
 * When the windows don't overlap, the reservation starts at opening time and
 * keeps its length (still capped at closing). Windows crossing midnight are
 * returned unchanged, since same-day minute comparisons don't apply to them.
 */
export function capTimeWindowToOpenHours(
  reservationWindow: TimeWindow,
  openHours: TimeWindow
): TimeWindow {
  const reservationStart = toMinutes(
    reservationWindow.startHour,
    reservationWindow.startMinutes
  );
  const reservationEnd = toMinutes(
    reservationWindow.endHour,
    reservationWindow.endMinutes
  );
  const openStart = toMinutes(openHours.startHour, openHours.startMinutes);
  const openEnd = toMinutes(openHours.endHour, openHours.endMinutes);

  if (reservationEnd <= reservationStart || openEnd <= openStart) {
    return reservationWindow;
  }

  const cappedStart = Math.max(reservationStart, openStart);
  const cappedEnd = Math.min(reservationEnd, openEnd);
  if (cappedEnd > cappedStart) {
    return toWindow(cappedStart, cappedEnd);
  }

  const reservationLength = reservationEnd - reservationStart;
  return toWindow(openStart, Math.min(openStart + reservationLength, openEnd));
}
